import { useEffect, useMemo, useState } from "react";
import {
  Check,
  PackageCheck,
  PackageOpen,
  PackagePlus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { accountApi, type TackleItem } from "../../account";

type PackingItem = TackleItem & {
  selected: boolean;
  trip_quantity: number;
  packed: boolean;
  packing_source: "manual" | "suggested" | null;
  packing_id: string | null;
  suggestion_score: number;
  suggestion_reason: string;
  suggested: boolean;
  reserved_elsewhere: number;
  available_for_trip: number;
  availability_reason: string;
};
type PackingPayload = {
  trip: {
    id: string;
    targetSpecies: string | null;
    status: string;
    editable: boolean;
  };
  items: PackingItem[];
  summary: { selected: number; packed: number };
  boundary: string;
};
const typeLabels: Record<TackleItem["tackle_type"], string> = {
  wobbler: "Vaappu",
  spinner: "Lippa",
  spoon: "Lusikka",
  jig: "Jigi",
  fly: "Perho",
  hook: "Koukku",
  other: "Muu",
};
const imageUrl = (item: PackingItem) =>
  `/api/account/tackle-items/${encodeURIComponent(item.id)}/image?v=${encodeURIComponent(item.updated_at)}`;

export function TripTacklePacking({ tripId }: { tripId: string }) {
  const [data, setData] = useState<PackingPayload | null>(null),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState("");
  const load = async () => {
    try {
      setData(
        await accountApi<PackingPayload>(
          `/api/account/trips/${encodeURIComponent(tripId)}/tackle`,
        ),
      );
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Pakkauslistaa ei voitu ladata.",
      );
    }
  };
  useEffect(() => {
    void load();
  }, [tripId]);
  const selected = data?.items.filter((item) => item.selected) ?? [],
    candidates = data?.items.filter((item) => !item.selected) ?? [],
    suggested = useMemo(
      () => candidates.filter((item) => item.suggested),
      [candidates],
    ),
    otherCandidates = candidates.filter((item) => !item.suggested),
    editable = Boolean(data?.trip.editable);
  const mutate = async (path: string, init: RequestInit, success: string) => {
    setBusy(path);
    setMessage("");
    try {
      setData(await accountApi<PackingPayload>(path, init));
      setMessage(success);
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Pakkauslistaa ei voitu päivittää.",
      );
    } finally {
      setBusy("");
    }
  };
  const add = (item: PackingItem) =>
    mutate(
      `/api/account/trips/${encodeURIComponent(tripId)}/tackle`,
      {
        method: "POST",
        body: JSON.stringify({ tackleItemId: item.id, quantity: 1 }),
      },
      `${item.name} lisättiin pakkauslistalle.`,
    );
  const addSuggestions = () =>
    mutate(
      `/api/account/trips/${encodeURIComponent(tripId)}/tackle`,
      {
        method: "POST",
        body: JSON.stringify({ action: "add_suggestions" }),
      },
      "Käytettävissä olevat omat ehdotukset lisättiin pakkauslistalle.",
    );
  const update = (
    item: PackingItem,
    input: { packed?: boolean; quantity?: number },
  ) =>
    mutate(
      `/api/account/trips/${encodeURIComponent(tripId)}/tackle/${encodeURIComponent(item.id)}`,
      { method: "PATCH", body: JSON.stringify(input) },
      input.packed === true
        ? `${item.name} merkittiin pakatuksi.`
        : input.packed === false
          ? `${item.name} avattiin pakattavaksi.`
          : "Määrä päivitettiin.",
    );
  const remove = (item: PackingItem) =>
    mutate(
      `/api/account/trips/${encodeURIComponent(tripId)}/tackle/${encodeURIComponent(item.id)}`,
      { method: "DELETE" },
      `${item.name} poistettiin reissulta.`,
    );
  if (!data)
    return (
      <section className="trip-tackle-packing">
        <div className="trip-neutral-state">
          <PackageOpen />
          <span>
            <b>{message || "Ladataan Kalapakkia…"}</b>
            <small>Omat välineet pysyvät yksityisinä.</small>
          </span>
        </div>
      </section>
    );
  return (
    <section
      className="trip-tackle-packing"
      aria-labelledby="trip-tackle-title"
    >
      <header>
        <div>
          <span className="eyebrow">Kalapakki mukana reissussa</span>
          <h3 id="trip-tackle-title">Pakkauslista</h3>
          <p>
            Valitse omista välineistä mukaan lähtevät määrät ja merkitse ne
            pakatuiksi.
          </p>
        </div>
        <div className="trip-packing-progress">
          <PackageCheck />
          <span>
            <b>
              {data.summary.packed}/{data.summary.selected}
            </b>
            <small>pakattu</small>
          </span>
        </div>
      </header>
      {message && (
        <p className="form-message trip-message" role="status">
          {message}
        </p>
      )}
      {selected.length ? (
        <div className="trip-packed-items">
          {selected.map((item) => (
            <article
              className={item.packed ? "packed" : undefined}
              key={item.id}
            >
              {item.image_id ? (
                <img src={imageUrl(item)} alt="" />
              ) : (
                <span className="trip-tackle-placeholder">
                  <PackageOpen />
                </span>
              )}
              <button
                className="trip-packed-toggle"
                type="button"
                aria-label={`${item.packed ? "Merkitse pakkaamatta" : "Merkitse pakatuksi"} ${item.name}`}
                disabled={Boolean(busy) || !editable}
                onClick={() => void update(item, { packed: !item.packed })}
              >
                {item.packed ? <Check /> : <span />}
              </button>
              <div>
                <small>
                  {typeLabels[item.tackle_type]}
                  {item.packing_source === "suggested" ? " · Oma ehdotus" : ""}
                </small>
                <h4>{item.name}</h4>
                <p>
                  {[item.brand, item.model, item.color]
                    .filter(Boolean)
                    .join(" · ") || "Oma väline"}
                </p>
                {item.reserved_elsewhere > 0 && (
                  <em className="trip-tackle-availability">
                    {item.reserved_elsewhere} kpl varattu muille samanaikaisille
                    reissuille
                  </em>
                )}
              </div>
              <label>
                Mukaan
                <select
                  aria-label={`${item.name} määrä`}
                  value={item.trip_quantity}
                  disabled={Boolean(busy) || !editable}
                  onChange={(event) =>
                    void update(item, { quantity: Number(event.target.value) })
                  }
                >
                  {Array.from(
                    { length: item.available_for_trip },
                    (_, index) => index + 1,
                  ).map((value) => (
                    <option value={value} key={value}>
                      {value} kpl
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="trip-tackle-remove"
                type="button"
                aria-label={`Poista ${item.name} pakkauslistalta`}
                disabled={Boolean(busy) || !editable}
                onClick={() => void remove(item)}
              >
                <Trash2 />
              </button>
            </article>
          ))}
        </div>
      ) : (
        <div className="trip-tackle-empty">
          <PackageOpen />
          <div>
            <b>Pakkauslista on vielä tyhjä</b>
            <p>
              Lisää alta omia välineitäsi. Kalapakin alkuperäiset määrät eivät
              muutu.
            </p>
          </div>
        </div>
      )}
      {suggested.length > 0 && (
        <section className="trip-tackle-suggestions">
          <header>
            <Sparkles />
            <div>
              <b>
                {data.trip.targetSpecies
                  ? `Omat ${data.trip.targetSpecies}-merkintäsi`
                  : "Omat suosikkisi"}
              </b>
              <small>
                Nostot perustuvat vain Kalapakkiin itse tallentamiisi tietoihin
              </small>
            </div>
            <button
              type="button"
              className="trip-add-suggestions"
              disabled={Boolean(busy) || !editable || !suggested.some((item) => item.available_for_trip > 0)}
              onClick={() => void addSuggestions()}
            >
              <PackagePlus />
              Lisää kaikki käytettävissä olevat
            </button>
          </header>
          <div>
            {suggested.map((item) => (
              <article key={item.id}>
                {item.image_id ? (
                  <img src={imageUrl(item)} alt="" />
                ) : (
                  <PackageOpen />
                )}
                <div>
                  <small>{item.suggestion_reason}</small>
                  <b>{item.name}</b>
                  <span>
                    {typeLabels[item.tackle_type]} · {item.available_for_trip}/
                    {item.quantity} kpl käytettävissä
                  </span>
                  {item.reserved_elsewhere > 0 && (
                    <em>{item.availability_reason}</em>
                  )}
                </div>
                <button
                  type="button"
                  disabled={
                    Boolean(busy) || item.available_for_trip < 1 || !editable
                  }
                  onClick={() => void add(item)}
                >
                  <PackagePlus />
                  Lisää mukaan
                </button>
              </article>
            ))}
          </div>
        </section>
      )}
      <section className="trip-tackle-inventory">
        <header>
          <div>
            <b>Kaikki käytettävissä olevat välineet</b>
            <small>{otherCandidates.length} muuta valitsematta</small>
          </div>
          <a href="/oma/kalapakki">Muokkaa Kalapakkia</a>
        </header>
        {otherCandidates.length ? (
          <div>
            {otherCandidates.map((item) => (
              <article key={item.id}>
                <div>
                  <b>{item.name}</b>
                  <small>
                    {typeLabels[item.tackle_type]} · {item.available_for_trip}/
                    {item.quantity} kpl käytettävissä
                  </small>
                </div>
                <button
                  type="button"
                  disabled={
                    Boolean(busy) || item.available_for_trip < 1 || !editable
                  }
                  onClick={() => void add(item)}
                >
                  <PackagePlus />
                  Lisää
                </button>
              </article>
            ))}
          </div>
        ) : (
          <p>Kaikki käytettävissä olevat välineet ovat jo pakkauslistalla.</p>
        )}
      </section>
      {!editable && (
        <p className="trip-tackle-readonly" role="status">
          Päättyneen, valmiin tai perutun reissun pakkauslista säilyy
          katseltavana, mutta sitä ei enää voi muuttaa.
        </p>
      )}
      <p className="trip-tackle-boundary">{data.boundary}</p>
    </section>
  );
}
